import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorService } from '@web/common';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { Guid, once } from '@web/utils';
import { HttpErrorResponse } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { AdminBoxDetailsForm } from '../../models/admin-box-details-form';
import { AdminBoxDataSourceService } from '../../services/admin-box-data-source.service';
import { AdminBoxDetailsFormComponent } from '../admin-box-details-form/admin-box-details-form.component';

@Component({
  selector: 'app-admin-box-edit',
  imports: [
    AlertsComponent,
    CommonModule,
    SpinnerFilledComponent,
    AdminBoxDetailsFormComponent,
  ],
  templateUrl: './admin-box-edit.component.html',
  styleUrl: './admin-box-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminBoxEditComponent {
  private readonly boxDataSource = inject(AdminBoxDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public readonly id = input.required<Guid>();
  public readonly submitSuccessEvent = output<void>();

  protected readonly id$ = toObservable(this.id);
  protected readonly form$ = this.formSource();

  public handleSubmitClickEvent(data: AdminBoxDetailsForm): void {
    this.boxDataSource
      .updateByFormData(this.id(), data)
      .pipe(once())
      .subscribe({
        next: () => this.notifySubmitSuccessEvent(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private notifySubmitSuccessEvent(): void {
    this.submitSuccessEvent.emit();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }

  private formSource(): Observable<AdminBoxDetailsForm> {
    return this.id$.pipe(
      switchMap((id) => this.boxDataSource.fetchDetailsFormById(id)),
    );
  }
}
